import { useState } from "react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Send, CheckCircle } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import WhatsAppNumbers from "@/components/WhatsAppNumbers";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactForm({ source = "general" }) {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, { ...form, source });
      setSent(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      setError(t("formError"));
    }
    setSending(false);
  };

  if (sent) {
    return (
      <motion.div className="flex flex-col items-center gap-4 py-16" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} data-testid="contact-success">
        <CheckCircle className="w-16 h-16 text-[#C8A951]" strokeWidth={1} />
        <h3 className="font-heading text-2xl text-white">{t("formSuccess")}</h3>
        <p className="text-[#A1A1AA] font-body text-center max-w-md">{t("formSuccessDesc")}</p>
        <button onClick={() => setSent(false)} className="text-[#C8A951] text-sm underline underline-offset-4 mt-2 font-body" data-testid="contact-send-another">{t("formAnother")}</button>
      </motion.div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* WhatsApp Numbers */}
      <div className="mb-10">
        <WhatsAppNumbers label={t("whatsappContact")} />
      </div>

      <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2 block font-body">{t("formName")}</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder={t("formNamePlaceholder")} className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-white/30 focus:border-[#C8A951] focus:ring-[#C8A951]/20 h-12" data-testid="contact-name-input" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2 block font-body">{t("formEmail")}</label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder={t("formEmailPlaceholder")} className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-white/30 focus:border-[#C8A951] focus:ring-[#C8A951]/20 h-12" data-testid="contact-email-input" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2 block font-body">{t("formPhone")}</label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t("formPhonePlaceholder")} className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-white/30 focus:border-[#C8A951] focus:ring-[#C8A951]/20 h-12" data-testid="contact-phone-input" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2 block font-body">{t("formSubject")}</label>
            <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required placeholder={t("formSubjectPlaceholder")} className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-white/30 focus:border-[#C8A951] focus:ring-[#C8A951]/20 h-12" data-testid="contact-subject-input" />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2 block font-body">{t("formMessage")}</label>
          <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required placeholder={t("formMessagePlaceholder")} rows={5} className="bg-[#1A1A1A] border-white/10 text-white placeholder:text-white/30 focus:border-[#C8A951] focus:ring-[#C8A951]/20 resize-none" data-testid="contact-message-input" />
        </div>
        {error && <p className="text-red-400 text-sm" data-testid="contact-error">{error}</p>}
        <button type="submit" disabled={sending} className="w-full sm:w-auto bg-[#C8A951] text-black font-medium tracking-wide px-10 py-4 hover:bg-white transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-50 font-body uppercase text-sm" data-testid="contact-submit-button">
          {sending ? t("formSending") : t("formSend")}
          <Send size={16} />
        </button>
      </form>
    </div>
  );
}
